import React, { forwardRef, useMemo } from 'react';
import { Dropdown as PrimeDropdown, DropdownProps } from 'primereact/dropdown';
import { ObjectUtils } from 'primereact/utils';

/**
 * PrimeReact's Dropdown with option labels guaranteed to be strings.
 *
 * PrimeReact 7.2.1 runs a keyboard type-ahead whenever a printable key is pressed while the
 * dropdown has focus — a plain letter, but also the `v` of a Ctrl+V paste. That search does:
 *
 *     var label = this.getOptionLabel(option).toLocaleLowerCase(this.props.filterLocale);
 *
 * (dropdown.esm.js:854) with no null check, while getOptionLabel returns whatever
 * resolveFieldData finds — `null` for an option whose label field is null. A SINGLE nullable
 * label anywhere in the list therefore white-screens the page on the next keypress. Labels come
 * from backend LOV data all over the app, so this is a whole class of crashes rather than one bug
 * (seen with the transportation-type list and folders). (Sentry RINGIL-APP-R7 / RINGIL-APP-W4.)
 *
 * This wrapper coerces a nullish resolved label to '' and drops null entries, which the renderer
 * cannot display anyway. Everything else is passed through untouched, and the original array is
 * returned unchanged when there is nothing to fix, so referential equality (and PrimeReact's own
 * change detection) is preserved for the overwhelmingly common healthy case.
 *
 * Import this instead of `primereact/dropdown` — the export is named `Dropdown`, so call sites
 * differ only in the module path.
 */

/** Sets `path` (which may be dotted) to '' on a shallow clone, leaving the input untouched. */
const withEmptyLabel = (option: Record<string, any>, path: string): Record<string, any> => {
  const keys = path.split('.');
  const clone = { ...option };
  let cursor: Record<string, any> = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    cursor[key] = cursor[key] != null ? { ...cursor[key] } : {};
    cursor = cursor[key];
  }
  cursor[keys[keys.length - 1]] = '';
  return clone;
};

const normalizeLabel = (option: any, optionLabel?: string): any => {
  // Primitive options (the plain-value mode) carry no label field to repair.
  if (typeof option !== 'object') return option;

  if (optionLabel) {
    const resolved = ObjectUtils.resolveFieldData(option, optionLabel);
    return resolved == null ? withEmptyLabel(option, optionLabel) : option;
  }
  // Without optionLabel PrimeReact uses option.label when the key EXISTS, and the option itself
  // otherwise — so only an explicitly null label needs fixing.
  return option.label === null ? { ...option, label: '' } : option;
};

const normalizeOptions = (options: any, optionLabel?: string, optionGroupLabel?: string, optionGroupChildren?: string): any => {
  if (!Array.isArray(options)) return options;

  let changed = false;
  const normalized: any[] = [];

  options.forEach(option => {
    if (option == null) {
      changed = true;
      return;
    }
    let next = optionGroupLabel ? normalizeLabel(option, optionGroupLabel) : normalizeLabel(option, optionLabel);

    if (optionGroupLabel) {
      const childrenKey = optionGroupChildren ?? 'items';
      const children = normalizeOptions(ObjectUtils.resolveFieldData(next, childrenKey), optionLabel);
      if (children !== ObjectUtils.resolveFieldData(next, childrenKey)) {
        next = { ...next, [childrenKey]: children };
      }
    }

    if (next !== option) changed = true;
    normalized.push(next);
  });

  return changed ? normalized : options;
};

export const Dropdown = forwardRef<PrimeDropdown, DropdownProps>((props, ref) => {
  const { options, optionLabel, optionGroupLabel, optionGroupChildren } = props;
  const safeOptions = useMemo(
    () => normalizeOptions(options, optionLabel, optionGroupLabel, optionGroupChildren),
    [options, optionLabel, optionGroupLabel, optionGroupChildren],
  );

  return <PrimeDropdown ref={ref} {...props} options={safeOptions} />;
});

Dropdown.displayName = 'Dropdown';

export type { DropdownProps };
